// Layer 4 (UI) — the Critical controls tab (polish.P20, comp A `controlsTable`,
// C's join): one row per interlock with its latest CV check beside it, No-one
// overrides first. P1 is an outlined ink pill; a No-one override is ink-ruled;
// a passing check is quiet (a tick), only the exceptions take colour. Below
// 1440 the performer chip sits under its check so all nine columns hold at 1280.
// Renders `criticalControls()` only.

import { Table, TBody, TD, TH, THead, TR } from '@/design-system';
import { cn } from '@/shared/cn';
import { DowntimeCard, SYN_CHIP } from '../downtime/DowntimeCard';
import { EDGE_TABLE } from '../maintenance/MaintenanceTable';
import { StatusMark, ToneChip } from '../process/ProcessUi';
import { criticalControls, type ControlResult } from './CriticalControls.transform';
import { IdCode, OwnerChip, REF_CHIP } from './risk-ui';

const OUTLINE_PILL =
  'inline-flex items-center whitespace-nowrap rounded-full border px-2 py-px text-[10px] font-semibold uppercase leading-4 tracking-[0.05em]';

function Priority({ priority }: { priority: 'P1' | 'P2' }) {
  return priority === 'P1'
    ? <span className={cn(OUTLINE_PILL, 'border-[var(--hairline-strong)] text-ink-primary')} data-priority="P1">P1</span>
    : <ToneChip tone="neutral" data-priority="P2">P2</ToneChip>;
}

function Override({ authority, noOne }: { authority: string; noOne: boolean }) {
  return (
    <span
      className={cn(OUTLINE_PILL, noOne ? 'border-ink-primary text-ink-primary' : 'border-[var(--hairline-strong)] text-ink-secondary')}
      data-override={authority}
    >
      {authority}
    </span>
  );
}

/** `08 Jun · Night shift`: one line from 1440, the shift under the day below it. */
function ShiftDate({ label }: { label: string }) {
  const [day, shift] = label.split(' · ');
  if (!shift) return <span className="whitespace-nowrap">{label}</span>;
  return (
    <span className="flex flex-col min-[1440px]:block min-[1440px]:whitespace-nowrap">
      <span className="whitespace-nowrap">{day}</span>
      <span className="hidden min-[1440px]:inline"> · </span>
      <span className="whitespace-nowrap text-xs text-muted min-[1440px]:text-[length:inherit] min-[1440px]:text-ink-secondary">{shift}</span>
    </span>
  );
}

function Result({ result, label }: { result: ControlResult; label: string }) {
  if (result === 'pass') return <StatusMark status={label} tone="ok" />;
  if (result === 'pass-late') return <ToneChip tone="neutral" data-status={label}>{label}</ToneChip>;
  return <ToneChip tone={result === 'degraded' ? 'warn' : 'bad'} data-status={label}>{label}</ToneChip>;
}

export function CriticalControls() {
  const rows = criticalControls();
  return (
    <DowntimeCard
      title="Critical controls"
      meta="interlock and its latest check · No one overrides first"
      actions={SYN_CHIP}
      className="px-0 pb-1 [&>header]:px-4"
    >
      <div className={cn(EDGE_TABLE, '[&_td]:!align-middle [&_td]:py-1.5')}>
        <Table wrap={false} className="tabular-nums">
          <THead>
            <TR>
              <TH>Interlock</TH>
              <TH>Pri</TH>
              <TH>Override</TH>
              <TH>Check</TH>
              <TH className="hidden min-[1440px]:table-cell">Performer</TH>
              <TH>Last checked</TH>
              <TH>Result</TH>
              <TH>Next due</TH>
              <TH>Raised</TH>
            </TR>
          </THead>
          <TBody>
            {rows.map((row) => (
              <TR key={row.interlock.id} data-control-row={row.interlock.id}>
                <TD className="min-w-[11rem] min-[1440px]:min-w-[12rem]">
                  <div className="text-[13px]">
                    <IdCode className="mr-1.5">{row.interlock.id}</IdCode>
                    <span className="font-semibold text-ink-primary">{row.interlock.equipment}</span>
                  </div>
                  <div className="text-xs leading-snug text-muted">{row.trigger}</div>
                </TD>
                <TD><Priority priority={row.interlock.priority} /></TD>
                <TD className="whitespace-nowrap">
                  <Override authority={row.interlock.overrideAuthority} noOne={row.noOneOverride} />
                  {row.interlock.pendingChange ? (
                    <div className="mt-1"><ToneChip tone="warn" data-change-pending={row.interlock.pendingChange.id}>Change pending</ToneChip></div>
                  ) : null}
                </TD>
                {row.check ? (
                  <>
                    <TD className="whitespace-nowrap">
                      <IdCode>{row.check.id}</IdCode>
                      <div className="text-xs text-muted">{row.check.tier}</div>
                      <div className="text-xs text-muted">{row.check.frequency}</div>
                      <div className="mt-1 min-[1440px]:hidden"><OwnerChip>{row.check.performer}</OwnerChip></div>
                    </TD>
                    <TD className="hidden min-[1440px]:table-cell"><OwnerChip>{row.check.performer}</OwnerChip></TD>
                    <TD><ShiftDate label={row.check.checkedLabel} /></TD>
                    <TD className="whitespace-nowrap" data-result={row.check.result}>
                      <Result result={row.check.result} label={row.check.resultLabel} />
                    </TD>
                    <TD><ShiftDate label={row.check.nextDueLabel} /></TD>
                    <TD className="whitespace-nowrap">
                      {row.check.resultingThreeWId ? <span className={REF_CHIP}>{row.check.resultingThreeWId}</span> : <span className="text-muted">—</span>}
                    </TD>
                  </>
                ) : (
                  <TD colSpan={6} />
                )}
              </TR>
            ))}
          </TBody>
        </Table>
      </div>
    </DowntimeCard>
  );
}
